
/**
 * DocumentType Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useDocumentTypeStore } from '@/store/document-type.store';
import { useTenantStore } from '@/store/tenant.store';

export class DocumentTypeIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch document types and update store
     */
    async fetchDocumentTypes(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', columnFilters = {}) {
        const store = useDocumentTypeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            const transformedData = (data || []).map(item => ({
                ...item,
                id: item._id || item.id
            }));
            store.setDocumentTypes(transformedData);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create document type
     */
    async createDocumentType(data) {
        const store = useDocumentTypeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const payload = { ...data, tenantId };
            const response = await this.service.create(payload);

            toast.success(i18next.t('documentType:messages.createSuccess', {
                defaultValue: 'Document type created',
            }));

            if (store.closeForm) store.closeForm();
            await this.fetchDocumentTypes(); // Refresh list

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update document type
     */
    async updateDocumentType(id, data) {
        const store = useDocumentTypeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('documentType:messages.updateSuccess', {
                defaultValue: 'Document type updated',
            }));

            if (store.closeForm) store.closeForm();
            await this.fetchDocumentTypes(); // Refresh list

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete document type
     */
    async deleteDocumentType(id) {
        const store = useDocumentTypeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('documentType:messages.deleteSuccess', {
                defaultValue: 'Document type deleted',
            }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();
            await this.fetchDocumentTypes(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete document types
     */
    async bulkDeleteDocumentTypes(ids) {
        const store = useDocumentTypeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.bulkDelete(ids);

            toast.success(i18next.t('documentType:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} document types deleted`,
            }));
            await this.fetchDocumentTypes(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
